import { AsyncZipDeflate, Zip, ZipPassThrough } from 'fflate';

// Formats that are already compressed.
// prettier-ignore
const STORED_EXTENSIONS = new Set([
  'jpg', 'jpeg', 'png', 'gif', 'webp', 'heic', 'heif', 'avif',
  'mp3', 'm4a', 'aac', 'ogg', 'oga', 'opus', 'webm', 'mp4', 'mov', 'mkv',
  'zip', 'rar', '7z', 'gz', 'tgz', 'bz2', 'xz',
  'pdf', 'docx', 'xlsx', 'pptx', 'odt', 'ods', 'odp', 'epub',
]);

const MIME_EXTENSIONS = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/gif': 'gif',
  'image/webp': 'webp',
  'image/heic': 'heic',
  'image/heif': 'heif',
  'image/avif': 'avif',
  'image/svg+xml': 'svg',
  'image/bmp': 'bmp',
  'application/pdf': 'pdf',
};

const EXTENSION_ALIASES = { jpeg: 'jpg', tif: 'tiff', htm: 'html' };

const MAX_NAME_LENGTH = 200;

export const MAX_ARCHIVE_BYTES = 50 * 1024 * 1024;

const FLUSH_BYTES = 8 * 1024 * 1024;

export class ArchiveTooLargeError extends Error {
  constructor(maxBytes) {
    super(`Attachments exceed ${Math.round(maxBytes / (1024 * 1024))} MB`);
    this.name = 'ArchiveTooLargeError';
    this.maxBytes = maxBytes;
  }
}

export const collectMessageAttachments = ({ images, files } = {}) => {
  const items = [];

  (Array.isArray(images) ? images : []).forEach((image, i) => {
    const key = typeof image === 'string' ? image : image?.key;
    const url = typeof image === 'string' ? null : image?.url || null;
    if (!key && !url) return;
    items.push({
      kind: 'image',
      key: key || null,
      url,
      name: (typeof image === 'object' && (image.fileName || image.name)) || null,
      fallbackName: `image-${i + 1}`,
    });
  });

  (Array.isArray(files) ? files : []).forEach((file, i) => {
    if (!file?.key && !file?.url) return;
    items.push({
      kind: 'file',
      key: file.key || null,
      url: file.url || null,
      name: file.fileName || file.name || null,
      extension: file.extension || null,
      fallbackName: `file-${i + 1}`,
    });
  });

  return items;
};

export const getExtension = (name) => {
  const match = /\.([a-z0-9]{0,7}[a-z][a-z0-9]{0,7})$/i.exec(name || '');
  return match && match[1].length <= 8 ? match[1].toLowerCase() : '';
};

const normalizeExtension = (ext) => {
  const value = String(ext || '')
    .trim()
    .replace(/^\./, '')
    .toLowerCase();
  return /^[a-z0-9]{1,8}$/.test(value) ? value : '';
};

const isSameExtension = (a, b) => (EXTENSION_ALIASES[a] || a) === (EXTENSION_ALIASES[b] || b);

const extensionFromMime = (mime) => {
  const type = (mime || '').split(';')[0].trim().toLowerCase();
  if (MIME_EXTENSIONS[type]) return MIME_EXTENSIONS[type];
  const [group, subtype] = type.split('/');
  return group === 'image' && /^[a-z0-9]+$/.test(subtype || '') ? subtype : '';
};

const trimName = (name) => name.replace(/^[\s.]+|[\s.]+$/g, '');

const sanitizeFileName = (name) =>
  trimName(
    String(name || '')
      // eslint-disable-next-line no-control-regex
      .replace(/[\u0000-\u001f<>:"/\\|?*]+/g, '_'),
  );

const truncate = (str, max) => {
  const chars = Array.from(str);
  return chars.length > max ? chars.slice(0, max).join('') : str;
};

export const resolveEntryName = (item, mime) => {
  const name = sanitizeFileName(item.name) || item.fallbackName;
  const current = getExtension(name);
  const explicit = normalizeExtension(item.extension);
  const wanted = explicit || extensionFromMime(mime);

  const keepCurrent = !!current && (!wanted || !explicit || isSameExtension(current, wanted));
  const ext = keepCurrent ? name.slice(name.length - current.length) : wanted;
  const stem = keepCurrent ? name.slice(0, -(current.length + 1)) : name;

  const maxStem = ext ? MAX_NAME_LENGTH - ext.length - 1 : MAX_NAME_LENGTH;
  const safeStem = trimName(truncate(stem, maxStem)) || item.fallbackName;
  return ext ? `${safeStem}.${ext}` : safeStem;
};

export const makeUniqueName = (name, taken) => {
  const ext = getExtension(name);
  const stem = ext ? name.slice(0, -(ext.length + 1)) : name;
  let candidate = name;
  for (let n = 1; taken.has(candidate.toLowerCase()); n += 1) {
    candidate = ext ? `${stem} (${n}).${ext}` : `${stem} (${n})`;
  }
  taken.add(candidate.toLowerCase());
  return candidate;
};

export const buildArchiveName = (date = new Date()) => {
  const pad = (n) => String(n).padStart(2, '0');
  const stamp =
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `_${pad(date.getHours())}-${pad(date.getMinutes())}`;
  return `attachments_${stamp}.zip`;
};

export const mapWithConcurrency = async (items, limit, worker) => {
  const results = new Array(items.length);
  let next = 0;
  const run = async () => {
    while (next < items.length) {
      const index = next;
      next += 1;
      results[index] = await worker(items[index], index);
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, run));
  return results;
};

const readBody = async (response, onChunk) => {
  if (!response.body?.getReader) {
    onChunk(new Uint8Array(await response.arrayBuffer()));
    return;
  }
  const reader = response.body.getReader();
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) return;
      if (value?.length) onChunk(value);
    }
  } catch (err) {
    reader.cancel().catch(() => {});
    throw err;
  }
};

export const buildAttachmentsArchive = async (
  items,
  resolveHref,
  { maxBytes = MAX_ARCHIVE_BYTES } = {},
) => {
  const parts = [];
  let pending = [];
  let pendingBytes = 0;
  const flush = () => {
    if (!pending.length) return;
    parts.push(new Blob(pending));
    pending = [];
    pendingBytes = 0;
  };

  let zipError = null;
  let settle;
  const done = new Promise((resolve, reject) => {
    settle = { resolve, reject };
  });
  done.catch(() => {});

  const zipper = new Zip((err, chunk, final) => {
    if (err) {
      zipError = err;
      settle.reject(err);
      return;
    }
    pending.push(chunk);
    pendingBytes += chunk.length;
    if (pendingBytes >= FLUSH_BYTES) flush();
    if (final) {
      flush();
      settle.resolve(new Blob(parts, { type: 'application/zip' }));
    }
  });

  const taken = new Set();
  const mtime = new Date();
  const failed = [];
  let total = 0;

  const openItem = async (item, label) => {
    const href = await resolveHref(item);
    if (!href) throw new Error(`No URL for attachment ${label}`);
    const response = await fetch(href);
    if (!response.ok) throw new Error(`Failed to fetch attachment ${label}`);
    return response;
  };

  const appendItem = async (item) => {
    const label = item.name || item.fallbackName;
    let response;
    try {
      response = await openItem(item, label);
    } catch (err) {
      console.warn(`Skipping attachment ${label}`, err);
      failed.push(label);
      return;
    }

    const declared = Number(response.headers.get('content-length')) || 0;
    if (total + declared > maxBytes) throw new ArchiveTooLargeError(maxBytes);

    const name = makeUniqueName(
      resolveEntryName(item, response.headers.get('content-type')),
      taken,
    );
    const entry = STORED_EXTENSIONS.has(getExtension(name))
      ? new ZipPassThrough(name)
      : new AsyncZipDeflate(name, { level: 6 });
    entry.mtime = mtime;
    zipper.add(entry);

    await readBody(response, (chunk) => {
      if (zipError) throw zipError;
      total += chunk.length;
      if (total > maxBytes) throw new ArchiveTooLargeError(maxBytes);
      entry.push(chunk);
    });
    entry.push(new Uint8Array(0), true);
  };

  try {
    for (const item of items) await appendItem(item);
    if (zipError) throw zipError;
    if (failed.length === items.length) throw new Error('Failed to fetch any attachment');
    zipper.end();
  } catch (err) {
    zipper.terminate();
    throw err;
  }

  return { blob: await done, failed };
};

export const saveBlob = (blob, fileName) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
};
